/**
 * Standard architectural floor-plan cut height (metres).
 * Crossing above this elevation auto-suggests dashed plan strokes; the user
 * can still change Style afterward.
 */
export const DEFAULT_PLAN_CUT_HEIGHT_M = 1.2

const CUT_HEIGHT_EPSILON = 1e-4

/** Dash pattern used for overhead / dashed plan strokes in 2D. */
export const PLAN_OVERHEAD_DASH = {
  dashed: true as const,
  dashSize: 0.15,
  gapSize: 0.1,
}

/** Minimal shape needed for cut-plane / line-type helpers. */
type CutPlaneSymbol = {
  threeD?: { baseElevation?: number } | null
  lineType?: string | null
}

/**
 * Reads a finite base elevation (distance from floor to object bottom).
 */
export function getPlanBaseElevation(symbol: CutPlaneSymbol): number {
  const baseElevation = symbol.threeD?.baseElevation
  return Number.isFinite(baseElevation) && typeof baseElevation === 'number'
    ? baseElevation
    : 0
}

/**
 * True when the object's bottom sits at or above the plan cut plane.
 */
export function isSymbolAbovePlanCutPlane(
  symbol: CutPlaneSymbol,
  cutHeightM: number = DEFAULT_PLAN_CUT_HEIGHT_M,
): boolean {
  return getPlanBaseElevation(symbol) >= cutHeightM - CUT_HEIGHT_EPSILON
}

/**
 * True when elevation moves from below the cut plane to at/above it.
 * Used as a one-shot trigger to suggest dashed line type.
 */
export function didCrossAbovePlanCutPlane(
  previousElevationM: number,
  nextElevationM: number,
  cutHeightM: number = DEFAULT_PLAN_CUT_HEIGHT_M,
): boolean {
  const threshold = cutHeightM - CUT_HEIGHT_EPSILON
  return previousElevationM < threshold && nextElevationM >= threshold
}

/**
 * One-shot smart default: when elevation crosses above the cut plane, suggest
 * dashed. Returns the line type to apply, or `null` when nothing should change
 * (already dashed, or no upward crossing).
 */
export function overheadLineTypeDefaultOnElevationChange(
  previousElevationM: number,
  nextElevationM: number,
  currentLineType?: string | null,
  cutHeightM: number = DEFAULT_PLAN_CUT_HEIGHT_M,
): 'dashed' | null {
  if (!didCrossAbovePlanCutPlane(previousElevationM, nextElevationM, cutHeightM)) {
    return null
  }
  if (currentLineType === 'dashed') {
    return null
  }
  return 'dashed'
}

/**
 * For newly inserted symbols that are already above the cut: default to dashed
 * unless the caller explicitly set a line type.
 */
export function defaultLineTypeForElevatedInsert(
  symbol: CutPlaneSymbol,
  explicitLineType: string | null | undefined,
  cutHeightM: number = DEFAULT_PLAN_CUT_HEIGHT_M,
): 'dashed' | undefined {
  if (explicitLineType === 'solid' || explicitLineType === 'dashed') {
    return undefined
  }
  if (!isSymbolAbovePlanCutPlane(symbol, cutHeightM)) {
    return undefined
  }
  return 'dashed'
}

/**
 * Stored plan line type (no hard override). Prefer this for rendering + panel.
 */
export function resolvePlanLineType(
  symbol: CutPlaneSymbol,
): 'solid' | 'dashed' {
  return symbol.lineType === 'dashed' ? 'dashed' : 'solid'
}

/**
 * Spread onto drei `<Line>` when the stored line type is dashed.
 */
export function planDashedLineProps(
  symbol: CutPlaneSymbol,
): typeof PLAN_OVERHEAD_DASH | Record<string, never> {
  return resolvePlanLineType(symbol) === 'dashed' ? PLAN_OVERHEAD_DASH : {}
}
